import { useMemo, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Copy, Check, UploadSimple, SpinnerGap } from "@phosphor-icons/react";
import { useCurrency } from "../context/CurrencyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildUpiUri({ upiId, name, amount, ref }) {
  const params = new URLSearchParams({
    pa: upiId,
    pn: name || "Vandy's",
    am: Number(amount).toFixed(2),
    cu: "INR",
    tn: ref || "Order Payment",
  });
  return `upi://pay?${params.toString()}`;
}

export default function UpiPaymentForm({ order, onSubmitted }) {
  const { formatPrice } = useCurrency();
  const [copied, setCopied] = useState(false);
  const [utr, setUtr] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const upiUri = useMemo(
    () => buildUpiUri({ upiId: order.upi_id, name: order.upi_name, amount: order.amount, ref: order.order_number }),
    [order]
  );

  const handleScreenshotChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setScreenshotFile(f);
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/upload/payment-screenshot`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScreenshotUrl(res.data.url);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to upload screenshot");
      setScreenshotFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleCopyUpi = async () => {
    try {
      await navigator.clipboard.writeText(order.upi_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!utr.trim()) {
      setErr("Please enter the UTR / transaction reference");
      return;
    }
    if (!screenshotUrl) {
      setErr("Please upload the payment screenshot");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/submit-payment`, {
        order_id: order.order_id || order.id,
        utr_number: utr.trim(),
        screenshot_url: screenshotUrl,
      });
      onSubmitted?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  const monoStyle = { fontFamily: "'Courier New', Courier, monospace" };

  return (
    <div className="space-y-6" data-testid="upi-payment-form">
      <div className="bg-[#EBD988] rounded-2xl p-6 md:p-8" data-testid="upi-payment-card">
        <h2 className="text-2xl text-[#1A1A1A] mb-1" style={{ fontFamily: "'Lazy Dog', cursive" }}>pay with UPI</h2>
        <p className="text-xs text-[#3a3a3a] mb-6" style={monoStyle}>
          scan the QR with any UPI app or copy the UPI ID
        </p>

        <div className="flex flex-col items-center gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl" data-testid="upi-qr-code">
            <QRCodeSVG value={upiUri} size={220} level="M" />
          </div>
          <p className="text-2xl text-[#E01A23]" style={{ fontFamily: "'Lazy Dog', cursive" }} data-testid="upi-amount">
            pay {formatPrice(order.amount)}
          </p>
          <p className="text-[10px] text-[#555] tracking-wider uppercase" style={monoStyle}>
            order #{order.order_number}
          </p>
        </div>

        <div className="bg-[#F5E6A0] rounded-xl p-3 flex items-center justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[#555] uppercase tracking-wider" style={monoStyle}>upi id</p>
            <p className="text-sm text-[#1A1A1A] truncate" data-testid="upi-id-text" style={monoStyle}>{order.upi_id}</p>
          </div>
          <button
            type="button"
            data-testid="copy-upi-btn"
            onClick={handleCopyUpi}
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-[#E01A23]/40 text-[#E01A23] hover:bg-[#E01A23] hover:text-white transition-colors text-xs"
            style={monoStyle}
          >
            {copied ? <><Check size={14} weight="bold" /> copied</> : <><Copy size={14} /> copy</>}
          </button>
        </div>

        <a href={upiUri} data-testid="upi-app-link" className="block text-center text-xs text-[#E01A23] hover:underline mb-2" style={monoStyle}>
          or tap here to open in a UPI app
        </a>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 bg-[#EBD988] rounded-2xl p-6 md:p-8" data-testid="payment-confirmation-form">
        <h3 className="text-lg text-[#1A1A1A]" style={{ fontFamily: "'Lazy Dog', cursive" }}>confirm your payment</h3>

        <div>
          <label className="text-xs text-[#3a3a3a] mb-1 block" style={monoStyle}>UTR / Transaction Reference *</label>
          <Input
            data-testid="utr-input"
            placeholder="e.g. 4123456789ABC"
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
            required
            className="bg-[#F5E6A0] border-[#D4C470] rounded-lg"
          />
          <p className="text-[10px] text-[#555] mt-1" style={monoStyle}>
            you'll find this in your UPI app's payment receipt
          </p>
        </div>

        <div>
          <label className="text-xs text-[#3a3a3a] mb-1 block" style={monoStyle}>Payment Screenshot *</label>
          <label
            htmlFor="screenshot-upload"
            data-testid="screenshot-upload-label"
            className="flex items-center justify-center gap-2 h-24 bg-[#F5E6A0] border-2 border-dashed border-[#D4C470] rounded-xl cursor-pointer hover:border-[#E01A23] transition-colors text-sm text-[#3a3a3a]"
            style={monoStyle}
          >
            {uploading ? (
              <><SpinnerGap size={18} className="animate-spin" /> uploading…</>
            ) : screenshotUrl ? (
              <><Check size={18} weight="bold" className="text-green-600" /> {screenshotFile?.name || "uploaded"}</>
            ) : (
              <><UploadSimple size={18} /> click to upload screenshot</>
            )}
            <input
              id="screenshot-upload"
              data-testid="screenshot-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleScreenshotChange}
            />
          </label>
        </div>

        {err && <p className="text-sm text-[#E01A23]" data-testid="submit-error">{err}</p>}

        <Button
          type="submit"
          data-testid="submit-payment-btn"
          disabled={submitting || uploading || !screenshotUrl || !utr.trim()}
          className="w-full bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-12 text-sm"
          style={monoStyle}
        >
          {submitting ? <><SpinnerGap size={16} className="animate-spin mr-2" /> submitting…</> : "submit payment"}
        </Button>
      </form>
    </div>
  );
}
